// ** React Imports
import { useState, useEffect, forwardRef } from 'react'

// ** Next Imports

// ** MUI Imports
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import { styled } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import TablaReferencia from 'src/@core/components/table/TablaReferencia'
import MuiTabList from '@mui/lab/TabList'

// ** Third Party Imports
import _ from 'lodash'

// ** Custom Components Imports
import { mergeList, globeList } from 'src/data/realms'

// ** Styled Components
import CustomHeader from 'src/@core/components/Header'
import { Button, ButtonGroup, Divider, Fade, InputBase, Paper } from '@mui/material'
import { green, indigo } from '@mui/material/colors'
import { ClearAll, Search } from '@mui/icons-material'

// ** Variables
/*
  Columnas para TablaReferencia. Antes eran columnas de DataGrid con renderCell;
  la unica diferencia real es que aca `formato` devuelve el JSX de la celda y el
  componente ya se ocupa de <th scope>, alineacion y tabular-nums.

  Se conservan los recuadros de color por globo: son la forma en que esta pagina
  distingue de que globo viene cada reino fusionado, y quitarlos para simplificar
  perderia informacion que el usuario usa.
*/
const Recuadro = ({ color, valor, ancho }) => (
  <Box
    sx={{
      backgroundColor: color,
      borderStyle: 'groove',
      borderColor: 'black',
      borderWidth: 'thin',
      minWidth: ancho,
      paddingLeft: '2px',
      paddingRight: '2px',
      display: 'inline-flex',
      justifyContent: 'center'
    }}
  >
    <Typography align='center' variant='caption' color='black'>
      {valor}
    </Typography>
  </Box>
)

const columnasTabla = [
  {
    clave: 'globe',
    encabezado: 'Globe',
    ordenable: true,
    ancho: 60,
    formato: (v, f) => <Recuadro color={f.realmColor} valor={v} ancho='32px' />
  },
  {
    clave: 'primaryRealm',
    encabezado: 'Realm',
    numerica: true,
    ordenable: true,
    ancho: 70,
    formato: (v, f) => <Recuadro color={f.realmColor} valor={v} ancho='45px' />
  },
  {
    clave: 'realmsMerged',
    encabezado: 'Realms Merged',
    formato: v =>
      v?.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '2px' }}>
          {[...v].sort((a, b) => a.realm - b.realm).map(r => (
            <Recuadro key={r.realm} color={r.color} valor={r.realm} ancho='28px' />
          ))}
        </Box>
      ) : (
        'None yet.'
      )
  },
  { clave: 'mergesQty', encabezado: 'Qty', numerica: true, ordenable: true, ancho: 60 },
  { clave: 'lastMerge', encabezado: 'Last Merge', ordenable: true, ancho: 110 }
]

// ** Custom Functions
const generateGlobeList = (globes, realms) => {
  const newItems = realms?.map(realm => {
    let realmGlobe
    const realmsMerged = []

    // Get primary realm globe and color
    globes.forEach(globe => {
      if (_.inRange(realm.primaryRealm, globe.from, globe.to + 1)) {
        realmGlobe = globe
      }
    })
    const mergesQty = realm.realmsMerged?.length ?? 0

    // Get merged realms globe and color
    if (realm?.realmsMerged?.length > 0) {
      realm.realmsMerged.forEach(rMerged => {
        globes.forEach(globe => {
          if (_.inRange(rMerged, globe.from, globe.to + 1)) {
            realmsMerged.push({ realm: rMerged, color: globe.color })
          }
        })
      })
    }

    return {
      ...realm,
      globe: realmGlobe?.globe,
      realmColor: realmGlobe?.color,
      mergesQty: mergesQty,
      realmsMerged: realmsMerged
    }
  })

  return newItems ?? []
}

 
const Realms = () => {
  // ** State
  const activeTab = 'items'
  const realmList = generateGlobeList(globeList, mergeList)
  const [itemList, setItemList] = useState(realmList)

  const [search, setSearch] = useState({ found: true, search: '' })

  useEffect(() => {
    const timeoutId = setTimeout(() => {}, 1500)

    return () => clearTimeout(timeoutId)
  }, [search])

  const handleFilterSearch = input => {
    const searchString = input.target.value
    if (searchString.length === 0 || searchString === null) {
      setItemList(realmList)
      setSearch({ found: true, search: searchString })

      return
    }

    const newRealmList = []

    realmList.forEach(realm => {
      const realmStr = realm.primaryRealm.toString().toLocaleLowerCase()

      const realmsMergedStr = realm.realmsMerged
        .map(r => r.realm)
        .join(' ')
        .toString()
        .toLocaleLowerCase()
      const searchData = `${realmStr} ${realmsMergedStr}`

      if (searchData.search(searchString.toLocaleLowerCase()) > -1) {
        newRealmList.push(realm)
      }
    })
    if (newRealmList?.length > 0) {
      setItemList(newRealmList)
      setSearch({ found: true, search: searchString ?? '' })
    } else {
      setItemList([])
      setSearch({ found: false, search: searchString ?? '' })
    }
  }

  const handleGlobeRealmsSearch = globe => {
    const globeObj = globeList.find(g => g.globe === globe)

    const newRealmList = []
    const loopTo = globeObj.to + 1

    for (let loopFrom = globeObj.from; loopFrom < loopTo; loopFrom++) {
      realmList.forEach(realm => {
        if (realm.realmsMerged.find(rm => rm.realm === loopFrom)) {
          newRealmList.push(realm)
        }
      })
    }

    const uniqueList = [...new Set(newRealmList.map(realm => realm.primaryRealm))]
    const newUniqueList = []

    uniqueList.forEach(uRealm => {
      const uRealmObj = realmList.find(fRealm => fRealm.primaryRealm === uRealm)
      if (uRealmObj) {
        newUniqueList.push(uRealmObj)
      }
    })

    if (newUniqueList?.length > 0) {
      setItemList(newUniqueList)
    }
  }

  const handleGlobeSearch = globe => {
    const newRealmList = [...realmList.filter(realm => realm.globe === globe)]

    if (newRealmList?.length > 0) {
      setItemList(newRealmList)
    }
  }

  const handleShowAllSearch = globe => {
    const newRealmList = [...realmList]

    if (newRealmList?.length > 0) {
      setItemList(newRealmList)
    }
  }


  const tabContentList = {
    items: (
      <>
        <Card>
          <Paper elevation={6} sx={{ padding: '1rem' }}>
            <Typography variant='body2' align='center'>
              REALMS GLOBES - LAST UPDATE: February 2, 2024.
            </Typography>
            <Divider sx={{ marginTop: '10px', marginBottom: '10px' }} />
            <Box key='wrapping-div' sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
              {globeList?.length > 0 && (
                <>
                  <Grid container spacing={3}>
                    {globeList.map(globe => {
                      return (
                        <Grid size={{ xs: 6, lg: 2 }}key={globe.globe}>
                          <ButtonGroup
                            key={globe.globe}
                            color='primary'
                            size='small'
                            sx={{ marginLeft: '10px', marginRight: '10px', marginBottom: '10px' }}
                          >
                            <Button
                              sx={{ backgroundColor: globe.color }}
                              aria-label={`${globe.globe}`}
                              onClick={e => {
                                handleGlobeSearch(globe.globe)
                              }}
                            >
                              {
                                <Typography variant='body2' color='black'>
                                  {globe.globe}
                                </Typography>
                              }
                            </Button>
                            <Button
                              aria-label={`from-to-${globe.globe}`}
                              onClick={e => {
                                handleGlobeRealmsSearch(globe.globe)
                              }}
                            >
                              <Typography variant='caption' color='textPrimary' sx={{ minWidth: '71px' }}>
                                {globe.from}
                                {' - '}
                                {globe.to}
                              </Typography>
                            </Button>
                          </ButtonGroup>
                        </Grid>
                      )
                    })}

                    <Grid size={{ xs: 6, lg: 2 }}key={'00'}>
                      <ButtonGroup
                        color='primary'
                        size='small'
                        sx={{ marginLeft: '10px', marginRight: '10px', marginBottom: '10px' }}
                      >
                        <Button
                          size='small'
                          variant='contained'
                          aria-label={`from-to-show-all`}
                          onClick={handleShowAllSearch}
                        >
                          SHOW ALL
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
            <Divider sx={{ marginTop: '10px', marginBottom: '15px' }} />
            <Paper
              elevation={6}
              component='form'
              sx={{
                padding: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                width: '250px',
                marginBottom: '10px',
                border: '1px solid rgb(49 113 235 / 39%)'
              }}
            >
              <InputBase
                sx={{
                  marginLeft: theme => theme.spacing(1),
                  flex: 1,
                  width: '75px'
                }}
                placeholder='Realm Filter'
                inputProps={{ 'aria-label': 'filter information' }}
                onChange={handleFilterSearch}
                value={search.search}
              />
              <IconButton
                aria-label='clear'
                onClick={e => {
                  setItemList([...realmList])
                  setSearch({ found: true, search: '' })
                }}
              >
                <ClearAll />
              </IconButton>
              <IconButton disabled aria-label='search'>
                <Search />
              </IconButton>
            </Paper>
            {!search.found && (
              <>
                <Divider />
                <div style={{ marginBottom: '10px' }}></div>

                <Typography variant='body2' align='center'>
                  {`There are not results for "${search.search}"`}
                </Typography>
              </>
            )}
          </Paper>
          {/*
            Antes era un DataGrid con pageSize 50. Virtualizado, o sea que no
            emitia ninguna fila en el HTML: las 214 fusiones de reino no existian
            en la respuesta del servidor. TablaReferencia las emite todas.

            clavesFiltro va vacio a proposito: el filtro de texto y el de globo ya
            los tiene esta pagina arriba, y `itemList` llega ya filtrado. En el
            primer render itemList es el dataset completo, asi que el HTML sale con
            las 214 filas y el cliente no rehidrata algo distinto.
          */}
          <TablaReferencia
            columnas={columnasTabla}
            filas={itemList}
            idFila={f => `${f.primaryRealm}-${f.globe}`}
            titulo='War and Order realm merge history, by globe'
            clavesFiltro={[]}
          />
        </Card>
      </>
    )
  }

  return (
    <>
      <Grid container spacing={6}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderTop: '6px solid',
              borderTopColor: 'primary.main'
            }}
          >
            <CustomHeader icon='realms' title='REALMS' />
            <Divider sx={{ marginY: '1rem' }} />
            <Typography variant='h6' component='h2' sx={{ fontWeight: 'bold', marginBottom: '1rem', color: 'primary.main' }}>
              Realms Overview
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              The Realms tool is a reference hub for War and Order server and realm information. Keep track of realm details, upcoming event schedules, merge timelines, and other key dates relevant to your servers.
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Maintain notes about the different realms you participate in, so you always have the context you need when planning cross-realm events and activities.
            </Typography>
          </Card>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Card>
            {tabContentList[activeTab]}
          </Card>
        </Grid>
      </Grid>
    </>
  )
}

Realms.noLoadingGate = true

export default Realms
